/**
 * commonConditions — 상위 행의 조건을 하위 행에 「공통 조건」으로 써 넣는 계산(순수 함수). 설계 FE-20260913 §3·§4.
 *
 * 오너 결정(2026-09-13): 검색은 언제나 **그 행에 저장된 조건 하나**로 한다. 공통 조건은 범위를 한정하는 보조 도구라서,
 * 버튼을 누른 순간 하위 행마다 저장 조건을 고쳐 쓴다(D1 「각각 항목에 저장」). 하위 행 조건은 공통과 상관없이 따로 바꿀 수 있다.
 * 그래서 「어느 항이 공통인가」를 따로 적어 두는 칸이 없다 — 상위 행의 **지금 저장 조건과 똑같은 항**이면 공통으로 본다(규칙 8).
 *
 * 반영 규칙(§4):
 *   1 조건 없는 행 — 공통 조건을 그대로 복사
 *   2 범위 한정 — 공통 항을 이 행의 모든 묶음에 「그리고」로 더한다
 *   3 또는 곱 — 공통 조건의 묶음이 여럿이면 (이 행 묶음 × 공통 묶음)
 *   4 값 하나 축(난이도·출처·AI 종류·작성자·문제 번호·정답/풀이/분석 여부)이 이 행에 이미 있으면 이 행 값을 둔다
 *   5 여러 값 축(개념·라벨·라벨 글자)은 둘 다 건다 — 똑같은 항은 한 번만. 상위 개념이 「정확히」면 이 행 개념을 둔다
 *   6 바꾸기 전 공통 조건(상위 행의 직전 저장본)과 똑같은 항을 먼저 빼고 새 공통 항을 더한다
 *   7 결과가 저장 검사(한도·모순)를 못 넘으면 그 행은 건너뛴다 — 쓰지 않는다
 */
import { FIELDS, EXPR_VERSION, inspectExpr } from './syllabusQuery';
import { ancestorsOf, childrenOf } from './treeIndex';

/** 한 문제가 값을 하나만 갖는 축 — 위아래에 다 있으면 하위(이 행) 값을 둔다(규칙 4) */
export const SINGLE_VALUE_FIELDS = Object.freeze([
  'difficulty', 'origin', 'ai_kind', 'author', 'problem', 'has_answer', 'has_solution', 'needs_analysis',
]);
const isSingle = (field) => SINGLE_VALUE_FIELDS.includes(field);

const groupsOf = (expr) => (Array.isArray(expr?.groups) ? expr.groups : []);
const termsOf = (group) => (Array.isArray(group?.terms) ? group.terms : []);
const cloneTerm = (t) => ({ ...t, values: [...(t.values || [])], ...(t.source ? { source: { ...t.source } } : {}) });

const normValue = (field, v) => {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  return FIELDS[field]?.kind === 'label' ? s.toLowerCase() : s;
};

/**
 * 항의 비교 열쇠 — 축 · 방식 · 값의 집합. 값의 순서와 순번 범위 유래(source)는 보지 않는다(규칙 8 「똑같은 항」).
 * 서버가 저장하며 값 중복을 없애고 라벨 uuid 를 소문자로 바꾸므로 여기서도 같게 편다.
 */
export function termKey(t) {
  if (!t || typeof t !== 'object') return '';
  const vals = [...new Set((Array.isArray(t.values) ? t.values : []).map((v) => JSON.stringify(normValue(t.field, v))))].sort();
  return `${t.field}|${t.match}|${vals.join(',')}`;
}
export const sameTerm = (a, b) => termKey(a) === termKey(b);

const groupKey = (terms) => terms.map(termKey).sort().join('&');
const dedupGroups = (groups) => {
  const seen = new Set();
  return groups.filter((g) => {
    const k = groupKey(g.terms);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
/** 뜻의 열쇠 — 항·묶음의 순서가 달라도 같은 식이면 같다(다시 반영해도 「변화 없음」이 되게) */
const meaningKey = (expr) => (expr == null ? 'null' : groupsOf(expr).map((g) => groupKey(termsOf(g))).sort().join('||'));
const allTerms = (expr) => groupsOf(expr).flatMap((g) => termsOf(g));
const uniqueTerms = (terms) => {
  const seen = new Set();
  return terms.filter((t) => {
    const k = termKey(t);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/**
 * 한 행에 공통 조건을 반영한 새 식.
 *   current  — 그 행의 저장 식(null = 조건 없음)
 *   common   — 상위 행의 새 공통 조건
 *   previous — 바꾸기 전 공통 조건(상위 행의 직전 저장본). 이것과 똑같은 항을 먼저 뺀다(규칙 6). 처음 반영이면 null
 * 반환 { status, next, skipped, reason }
 *   status — 'added'(조건 없던 행에 복사) · 'changed' · 'partial'(값 하나 축을 이 행 값으로 둠) · 'unchanged' · 'skipped'(쓰지 않음)
 *   next — 보낼 식(저장 규칙으로 정규화). 'unchanged' 면 current, 'skipped' 면 null
 *   skipped — 이 행 값을 두느라 넣지 않은 공통 축 이름들
 *   reason — 'skipped' 일 때 저장 검사 오류(ExprCheckError: 한도 · 모순 · 구조)
 */
export function applyCommon({ current = null, common = null, previous = null } = {}) {
  const commonGroups = groupsOf(common).map((g) => termsOf(g)).filter((ts) => ts.length);
  if (!commonGroups.length) return { status: 'unchanged', next: current, skipped: [], reason: null };
  const skipped = new Set();
  let next;
  if (current == null || !groupsOf(current).length) {
    next = { version: common.version ?? EXPR_VERSION, groups: commonGroups.map((ts) => ({ terms: ts.map(cloneTerm) })) };
  } else {
    const oldKeys = new Set(allTerms(previous).map(termKey));
    const base = dedupGroups(groupsOf(current).map((g) => ({
      terms: termsOf(g).filter((t) => !oldKeys.has(termKey(t))).map(cloneTerm),
    })));
    const out = [];
    base.forEach((g) => {
      commonGroups.forEach((ct) => {
        const terms = g.terms.map(cloneTerm);
        const have = new Set(terms.map(termKey));
        const fields = new Set(terms.map((t) => t.field));
        ct.forEach((t) => {
          const k = termKey(t);
          if (have.has(k)) return;
          if (isSingle(t.field) && fields.has(t.field)) { skipped.add(t.field); return; }
          if (t.field === 'concept' && t.match === 'exact' && fields.has('concept')) { skipped.add('concept'); return; }
          terms.push(cloneTerm(t));
          have.add(k);
        });
        out.push({ terms });
      });
    });
    next = { version: current.version ?? EXPR_VERSION, groups: dedupGroups(out) };
  }
  const insp = inspectExpr(next, { mode: 'store' });
  if (!insp.ok) return { status: 'skipped', next: null, skipped: [...skipped], reason: insp.error };
  if (current != null && meaningKey(insp.normalized) === meaningKey(current)) {
    return { status: 'unchanged', next: current, skipped: [...skipped], reason: null };
  }
  let status = current == null || !groupsOf(current).length ? 'added' : 'changed';
  if (status === 'changed' && skipped.size) status = 'partial';
  return { status, next: insp.normalized, skipped: [...skipped], reason: null };
}

/** 하위 행 id — 나무에 보이는 순서(전위: 부모 다음 그 자식들, 형제는 순번 순) */
function preorder(index, id) {
  const out = [];
  const seen = new Set([String(id)]);
  const walk = (k, depth) => {
    childrenOf(index, k).forEach((c) => {
      if (seen.has(c)) return;
      seen.add(c);
      out.push({ id: c, depth });
      walk(c, depth + 1);
    });
  };
  walk(String(id), 1);
  return out;
}

/**
 * 상위 행 하나의 공통 조건을 하위 행 전부(손자까지)에 반영할 계획 — 쓰기는 하지 않는다(설계 FE-20260913 §3).
 *   serverQueryOf(id) — 그 행의 **서버 저장 식**(초안이 아니라 — 쓰는 대상이 원본이다)
 *   isDirty(id) — 저장 전 변경이 있는가. 있으면 건너뛴다 — 반영이 원본을 바꾸면 나중에 그 초안을 저장할 때 공통 조건을 덮는다
 * 반환 [{ id, name, depth(1=바로 아래), current, status, next, skipped, reason }] — 나무 순서. status 는 applyCommon 과
 * 같고, 저장 전 변경으로 건너뛴 행은 status 'skipped' · reason 'draft'. current 는 반영 전 저장 식(창이 전후를 보인다).
 */
export function planCommonApply({
  index, parentId, common, previous = null, serverQueryOf, isDirty = () => false,
}) {
  return preorder(index, parentId).map(({ id, depth }) => {
    const name = index.byId.get(id)?.name || '';
    const current = serverQueryOf(id);
    if (isDirty(id)) {
      return {
        id, name, depth, current, status: 'skipped', next: null, skipped: [], reason: 'draft',
      };
    }
    const r = applyCommon({ current, common, previous });
    return {
      id, name, depth, current, ...r,
    };
  });
}

/** 반영 전후의 차이 — 빠지는 항 · 더해지는 항(모든 묶음을 모아 항 단위로). 창의 칩이 쓴다 */
export function termDiff(current, next) {
  const before = uniqueTerms(allTerms(current));
  const after = uniqueTerms(allTerms(next));
  const beforeKeys = new Set(before.map(termKey));
  const afterKeys = new Set(after.map(termKey));
  return {
    removed: before.filter((t) => !afterKeys.has(termKey(t))),
    added: after.filter((t) => !beforeKeys.has(termKey(t))),
    kept: after.filter((t) => beforeKeys.has(termKey(t))),
  };
}

/** 계획에서 실제로 쓸 행(바뀜 · 새로 걸림 · 일부 적용) */
export const writableRows = (plan) => (plan || []).filter((r) => r.status === 'added' || r.status === 'changed' || r.status === 'partial');

/**
 * 가장 가까운 상위 행 가운데 저장 조건이 있는 행 — 「공통 조건 · 상위 ○○」 줄의 출처. 없으면 null.
 * queryOf 는 **서버 저장 식**을 주는 함수(상위 행의 초안은 공통 조건이 아니다 — 반영하려면 먼저 저장한다)
 */
export function nearestCommonSource(index, id, queryOf) {
  const ups = ancestorsOf(index, id);
  for (let i = ups.length - 1; i >= 0; i -= 1) {
    const q = queryOf(ups[i]);
    if (q != null && allTerms(q).length) return { id: ups[i], name: index.byId.get(ups[i])?.name || '', query: q };
  }
  return null;
}

/**
 * 「공통」 꼬리표 판정기(규칙 8) — (항) → 그 항을 준 상위 행 이름 | null.
 * 가장 가까운 상위 행부터 본다. 이 행에서 칩을 고치면 똑같지 않게 되어 꼬리표가 사라진다.
 */
export function commonOriginOf(index, id, queryOf) {
  const ups = ancestorsOf(index, id).slice().reverse()
    .map((u) => ({ name: index.byId.get(u)?.name || '', keys: new Set(allTerms(queryOf(u)).map(termKey)) }))
    .filter((u) => u.keys.size);
  return (term) => {
    if (!ups.length || !term) return null;
    const k = termKey(term);
    const hit = ups.find((u) => u.keys.has(k));
    return hit ? hit.name : null;
  };
}

/**
 * 공통 조건이 이 행에 얼마나 들어 있나. 항은 이 행의 **모든 묶음**에 있어야 「들어 있음」이다.
 * 값 하나 축을 이 행이 따로 정해 두었으면(모든 묶음에 그 축이 있음) 「이 행에서 바꿈」으로 센다 — 빠진 것이 아니다.
 * 반환 { total, present, overridden, missing: [항] }
 */
export function commonCoverage(common, current) {
  const terms = uniqueTerms(allTerms(common));
  const groups = groupsOf(current);
  let present = 0;
  let overridden = 0;
  const missing = [];
  terms.forEach((t) => {
    if (groups.length && groups.every((g) => termsOf(g).some((x) => sameTerm(x, t)))) { present += 1; return; }
    const own = groups.length && groups.every((g) => termsOf(g).some((x) => x.field === t.field));
    if (own && (isSingle(t.field) || (t.field === 'concept' && t.match === 'exact'))) { overridden += 1; return; }
    missing.push(t);
  });
  return {
    total: terms.length, present, overridden, missing,
  };
}
